import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FormError } from '@/core/FormError.atom';
import { IconWithPopoverTooltip } from '@/core/IconWithPopoverTooltip.molecule';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { TooltipPosition } from '@seeqdev/qomponents';

export interface FormFieldWrapperProps {
  wrapperXstyle?: stylex.StyleXStyles;
  labelXstyle?: stylex.StyleXStyles;
  fieldXstyle?: stylex.StyleXStyles;
  testId?: string;
  children: React.ReactElement;
  label?: string | React.ReactNode;
  labelIcon?: string;
  labelIconAction?: () => void;
  labelIconTooltip?: string;
  labelClassNames?: string;
  id?: string;
  extraClassNames?: string;
  wrapperClassNames?: string;
  showError?: boolean;
  customErrorText?: string;
  customErrorParams?: Record<string, any>;
  tooltip?: string;
  tooltipPlacement?: TooltipPosition;
  onTooltipClick?: () => void;
  popoverContent?: React.ReactNode;
  errorClassNames?: string;
  warningText?: string;
  warningTextParams?: Record<string, string | number>;
  labelIconClassNames?: string;
}

export const FormFieldWrapper = ({
  label,
  wrapperXstyle,
  labelXstyle,
  fieldXstyle,
  extraClassNames,
  testId = 'formComponent',
  children,
  showError,
  customErrorText,
  customErrorParams = {},
  wrapperClassNames,
  tooltip,
  tooltipPlacement,
  onTooltipClick,
  popoverContent,
  id,
  errorClassNames,
  labelIcon,
  labelIconAction,
  labelIconTooltip,
  labelClassNames,
  warningText,
  warningTextParams,
  labelIconClassNames,
}: FormFieldWrapperProps) => {
  const { t } = useTranslation();

  const renderLabel = () => {
    if (typeof label !== 'string') {
      return label;
    }

    const labelTestId = `${testId}Label`;

    if (label && (tooltip || popoverContent)) {
      return (
        <div {...stylex.props(formFieldWrapperStyles.tooltipLabel)} data-testid={labelTestId}>
          <span
            {...stylex.props(formFieldWrapperStyles.labelWithTooltip, labelXstyle)}
            className={clsx(
              stylex.props(formFieldWrapperStyles.labelWithTooltip, labelXstyle).className,
              labelClassNames,
            )}>
            {t(label)}
          </span>
          {tooltip ? (
            <Icon
              extraClassNames={labelIconClassNames}
              icon={faQuestionCircle}
              large={true}
              tooltip={t(tooltip)}
              testId={`${labelTestId}_icon`}
              tooltipPlacement={tooltipPlacement}
              onClick={onTooltipClick}
            />
          ) : (
            <IconWithPopoverTooltip placement="top" testId={`${labelTestId}Popover`} content={popoverContent} />
          )}
        </div>
      );
    }

    return (
      <span
        {...stylex.props(formFieldWrapperStyles.label, labelXstyle)}
        className={clsx(stylex.props(formFieldWrapperStyles.label, labelXstyle).className, labelClassNames)}
        data-testid={labelTestId}>
        {t(label)}
        {labelIcon && (
          <Icon
            extraClassNames={labelIconClassNames}
            icon={labelIcon}
            large={true}
            onClick={labelIconAction}
            tooltip={labelIconTooltip ? t(labelIconTooltip) : ''}
          />
        )}
      </span>
    );
  };

  const renderChildren = () => {
    if (!label && tooltip) {
      return (
        <div {...stylex.props(formFieldWrapperStyles.unlabeledField)}>
          {children}
          <Icon
            icon={faQuestionCircle}
            large={true}
            tooltip={t(tooltip)}
            tooltipPlacement={tooltipPlacement}
            xstyle={formFieldWrapperStyles.helpIcon}
            onClick={onTooltipClick}
          />
        </div>
      );
    }

    return children;
  };

  return (
    <div
      id={id}
      {...stylex.props(formFieldWrapperStyles.wrapper, wrapperXstyle)}
      className={clsx(stylex.props(formFieldWrapperStyles.wrapper, wrapperXstyle).className, wrapperClassNames)}
      data-testid={testId}>
      {renderLabel()}
      <div
        {...stylex.props(fieldXstyle)}
        className={clsx('fieldWrapper', stylex.props(fieldXstyle).className, extraClassNames)}
        data-testid={`${testId}FieldWrapper`}>
        {renderChildren()}
      </div>
      {showError && <span data-testid="showerror" />}
      {showError && customErrorText && (
        <FormError
          errorText={customErrorText}
          errorParameters={customErrorParams}
          dataTestId={`${testId}Error`}
          xstyle={errorClassNames == null ? formFieldWrapperStyles.error : undefined}
          extraClassNames={errorClassNames}
        />
      )}
      {warningText && (
        <p {...stylex.props(formFieldWrapperStyles.warning)}>
          {warningTextParams ? t(warningText, warningTextParams) : t(warningText)}
        </p>
      )}
    </div>
  );
};

export const formFieldWrapperStyles = stylex.create({
  helpIcon: { marginLeft: 5 },
  error: { marginTop: 5, marginBottom: 0 },
  wrapper: { display: 'flex', flexDirection: 'column', marginBottom: '.5rem' },
  tooltipLabel: { display: 'flex', flexDirection: 'row', marginBottom: '.3125rem', alignItems: 'center' },
  labelWithTooltip: { marginRight: '.3125rem' },
  label: { marginBottom: '.3125rem' },
  unlabeledField: { display: 'flex', flexDirection: 'row', flex: '1 1 auto', overflow: 'hidden', alignItems: 'center' },
  warning: { color: colors.warning, marginTop: '.3125rem', marginBottom: 0 },
});
